import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { ExportMenu, ListPageShell, PageHeader, Table } from '~/components/data';
import { Button, Select } from '~/components/primitives';
import { StatusBadge, type StatusBadgeStatus } from '~/components/status';
import { fetchLandedCost } from '~/lib/api/landed-cost';
import { useListPageState } from '~/lib/hooks/useListPageState';
import { useActiveEntityStore } from '~/store/active-entity';

// FE-P2-2 (QA 2026-06-04): LC state → StatusBadge variant.
const LC_STATE_BADGE: Record<string, StatusBadgeStatus> = {
  draft: 'needs_review',
  pending_allocation: 'in_progress',
  allocated: 'completed_draft',
  cancelled: 'failed',
};
import shared from './_auth._shared.module.css';

export const Route = createFileRoute('/_auth/landed-cost/')({ component: LandedCostList });

function LandedCostList(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const q = useQuery({
    queryKey: ['landed-cost', { status: statusFilter }],
    queryFn: () => fetchLandedCost(statusFilter ? { status: statusFilter } : {}),
    staleTime: 20_000,
  });
  const state = useListPageState(q);
  // F-sync — колонка «Компания» только в агрегатном охвате (холдинг/«Все компании»).
  const aggregateScope = useActiveEntityStore((s) => s.activeScope) !== 'entity';
  const entities = q.data?.entities ?? {};

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.nav_landed_cost()}
        description={m.route_lc_list_description()}
        actions={
          <div className={shared.actions}>
            <ExportMenu
              resource="landed-cost"
              filters={statusFilter ? { status: statusFilter } : {}}
            />
            <Link to="/landed-cost/new">
              <Button leadingIcon={<Plus size={14} />}>{m.route_lc_list_btn_new()}</Button>
            </Link>
          </div>
        }
      />

      <div className={shared.filters}>
        <Select
          label={m.route_runs_index_filter_status()}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={[
            { value: '', label: m.route_runs_index_filter_all() },
            { value: 'draft', label: m.route_lc_status_label({ status: 'draft' }) },
            { value: 'submitted', label: m.route_lc_status_label({ status: 'submitted' }) },
            { value: 'applied', label: m.route_lc_status_label({ status: 'applied' }) },
            { value: 'cancelled', label: m.route_lc_status_label({ status: 'cancelled' }) },
          ]}
        />
      </div>

      <ListPageShell
        state={state}
        emptyTitle={m.route_lc_list_empty_title()}
        emptyDescription={m.route_lc_list_empty_description()}
        emptyAction={
          <Link to="/landed-cost/new">
            <Button variant="primary" leadingIcon={<Plus size={14} />}>
              {m.route_lc_list_btn_new()}
            </Button>
          </Link>
        }
      >
        {(rows) => (
          <Table
            ariaLabel={m.route_lc_list_aria()}
            data={rows}
            rowKey={(v) => v.id}
            columns={[
              {
                // S-FIX-2-FE.3.2: canonical schema — `voucher_no` вместо `code`.
                id: 'code',
                header: m.route_lc_list_col_code(),
                accessor: (v) => (
                  <Link to="/landed-cost/$id" params={{ id: v.id }} className={shared.link}>
                    <span className={shared.mono}>{v.voucher_no}</span>
                  </Link>
                ),
              },
              ...(aggregateScope
                ? [
                    {
                      id: 'entity',
                      header: m.project_col_entity(),
                      accessor: (v: (typeof rows)[number]) =>
                        entities[v.entity_id ?? '']?.name ?? '—',
                      width: '180px',
                    },
                  ]
                : []),
              {
                // S-FIX-2-FE.3.2: computed `state` (draft / pending_allocation / allocated)
                // вместо legacy `status`. Backend handler S-FIX-2-BE.6.2 их вычисляет.
                // FE-226: localize raw enum через LANDED_COST_STATE_LABELS map.
                id: 'state',
                header: m.route_lc_list_col_status(),
                accessor: (v) => (
                  <StatusBadge
                    status={LC_STATE_BADGE[v.state as string] ?? 'needs_review'}
                    size="sm"
                  />
                ),
              },
              {
                id: 'overhead',
                header: m.route_lc_list_col_overhead(),
                // S-FIX-2-FE.3.2: total_overhead never NULL (BE.6.1 COALESCE).
                accessor: (v) => v.total_overhead ?? '0',
                mono: true,
                align: 'right',
              },
              {
                id: 'created',
                header: m.route_lc_list_col_created(),
                accessor: (v) => v.created_at?.slice(0, 10) ?? '—',
                mono: true,
              },
            ]}
          />
        )}
      </ListPageShell>
    </div>
  );
}
